import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import type { Payment, PaymentSortField, PaymentSortState } from '@models/payments';
import { CaptureStates } from '@models/payments';
import { CAPTURE_STATE_LABELS } from '@features/payments/constants';
import {
  formatDeclineLine,
  formatPaymentAmount,
  formatPaymentDate,
  getCustomerProfileUrl,
  getDeclineInfo,
  getPaymentCustomer,
  getPaymentMethodMaskedDigits,
  getPaymentSubscriptionLabel,
  getPaymentTypeLabel,
  getUiPaymentStatus,
} from '@features/payments/helpers';

import LoadingAnimation from '@components/LoadingAnimation';
import PaymentStatusBadge from '../PaymentStatusBadge';
import PaymentMethodChip from '../PaymentMethodChip';
import s from './PaymentsTable.module.css';

type SortableHeaderProps = {
  label: string;
  field: PaymentSortField;
  sort: PaymentSortState;
  onSort: (field: PaymentSortField) => void;
  align?: 'left' | 'right';
};

function SortableHeader({ label, field, sort, onSort, align = 'left' }: SortableHeaderProps) {
  const active = sort.field === field;
  return (
    <th
      className={`${s.sortable} ${align === 'right' ? s.alignRight : ''} ${active ? s.sortActive : ''}`}
      onClick={() => onSort(field)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <span className={`${s.sortInner} ${align === 'right' ? s.sortInnerRight : ''}`}>
        {label}
        <span className={s.sortIcons}>
          <ArrowDropUpIcon
            className={`${s.sortArrow} ${active && sort.dir === 'asc' ? s.sortArrowActive : ''}`}
            sx={{ fontSize: 16 }}
          />
          <ArrowDropDownIcon
            className={`${s.sortArrow} ${active && sort.dir === 'desc' ? s.sortArrowActive : ''}`}
            sx={{ fontSize: 16 }}
          />
        </span>
      </span>
    </th>
  );
}

const COL_WIDTHS = ['14%', '9%', '8%', '11%', '14%', '7%', '10%', '8%', '6%', '9%', '4%'];
const COL_WIDTHS_WITH_METHOD = [
  '13%',
  '8%',
  '7%',
  '7%',
  '10%',
  '13%',
  '7%',
  '10%',
  '8%',
  '6%',
  '7%',
  '4%',
];
const COL_WIDTHS_NO_PAYMENT_ID = ['11%', '8%', '12%', '17%', '8%', '11%', '8%', '7%', '11%', '7%'];
const COL_WIDTHS_WITH_METHOD_NO_PAYMENT_ID = [
  '10%',
  '8%',
  '8%',
  '10%',
  '15%',
  '8%',
  '10%',
  '8%',
  '7%',
  '10%',
  '6%',
];

const NOTABLE_CAPTURE_STATES = [
  CaptureStates.AWAITING,
  CaptureStates.REQUESTED,
  CaptureStates.EXPIRED,
] as const;

function getColWidths(showMethodColumn: boolean, hidePaymentIdColumn: boolean): string[] {
  if (showMethodColumn && hidePaymentIdColumn) return COL_WIDTHS_WITH_METHOD_NO_PAYMENT_ID;
  if (showMethodColumn) return COL_WIDTHS_WITH_METHOD;
  if (hidePaymentIdColumn) return COL_WIDTHS_NO_PAYMENT_ID;
  return COL_WIDTHS;
}

type PaymentsTableProps = {
  isLoading: boolean;
  isFetching: boolean;
  rows: Payment[];
  sort: PaymentSortState;
  onSort: (field: PaymentSortField) => void;
  onRowClick?: (row: Payment) => void;
  showMethodColumn?: boolean;
  hidePaymentIdColumn?: boolean;
};

function PaymentsTable({
  isLoading,
  isFetching,
  rows,
  sort,
  onSort,
  onRowClick,
  showMethodColumn = false,
  hidePaymentIdColumn = false,
}: PaymentsTableProps) {
  const showOverlay = isFetching && !isLoading && rows.length > 0;
  const colWidths = getColWidths(showMethodColumn, hidePaymentIdColumn);
  const colCount = colWidths.length;
  const rowsClickable = hidePaymentIdColumn && !!onRowClick;

  return (
    <div className={s.tableShell}>
      <div className={s.tableWrap}>
        <table className={s.table}>
          <colgroup>
            {colWidths.map((width, i) => (
              <col key={i} style={{ width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {!hidePaymentIdColumn && <th>Payment ID</th>}
              <th>Provider ID</th>
              <th>Payment method</th>
              {showMethodColumn && <th>Method</th>}
              <SortableHeader label="Date" field="date" sort={sort} onSort={onSort} />
              <th>Customer</th>
              <th>Type</th>
              <th>Descriptor</th>
              <th>Subscription</th>
              <th>Brand</th>
              <SortableHeader label="Amount" field="amount" sort={sort} onSort={onSort} />
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={colCount} className={s.loadingCell}>
                  <LoadingAnimation compact />
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className={s.empty}>
                  No payments match your search.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const customer = getPaymentCustomer(row);
                const typeLabel = getPaymentTypeLabel(row);
                const uiStatus = getUiPaymentStatus(row);
                // Only the states an operator can act on — captured/migrated/not-applicable rows
                // are the common case and would just add noise to every row.
                const captureState = NOTABLE_CAPTURE_STATES.includes(row.captureState as never)
                  ? row.captureState
                  : undefined;
                const amountLabel = formatPaymentAmount(
                  row.amount,
                  row.currency,
                  row.currency.decimalDigits,
                );
                const fd = formatPaymentDate(new Date(row.createdAt));
                const declineInfo = getDeclineInfo(row);
                const declineLine = declineInfo ? formatDeclineLine(declineInfo) : null;

                return (
                  <tr
                    key={row.id}
                    data-status={uiStatus}
                    className={rowsClickable ? s.rowClickable : undefined}
                    onClick={rowsClickable ? () => onRowClick?.(row) : undefined}>
                    {!hidePaymentIdColumn && (
                      <td className={s.paymentIdCell}>
                        <button
                          type="button"
                          className={s.idChip}
                          onClick={() => onRowClick?.(row)}
                          aria-label={`View details for payment ${row.id}`}
                          title="View payment details">
                          <span className={s.idChipText}>{row.id}</span>
                          <ChevronRightIcon
                            className={s.idChipChevron}
                            sx={{ fontSize: 18 }}
                            aria-hidden
                          />
                        </button>
                      </td>
                    )}
                    <td>
                      <span className={s.providerIdChip} title={row.providerId}>
                        {row.providerId || ''}
                      </span>
                    </td>
                    <td>
                      <PaymentMethodChip
                        customerPaymentMethod={row.customerPaymentMethod}
                        processor={row.processor}
                      />
                    </td>
                    {showMethodColumn && (
                      <td>
                        {(() => {
                          const maskedDigits = getPaymentMethodMaskedDigits(row);
                          return maskedDigits ? (
                            <span className={s.methodDigits}>{maskedDigits}</span>
                          ) : (
                            <span className={s.brandCodeEmpty}>N/A</span>
                          );
                        })()}
                      </td>
                    )}
                    <td>
                      <div className={s.dateMain}>{fd.date}</div>
                      <div className={s.dateSub}>{fd.time}</div>
                    </td>
                    <td>
                      {(() => {
                        const profileUrl = getCustomerProfileUrl(customer);
                        return profileUrl ? (
                          <a
                            href={profileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.customerLink}
                            onClick={(e) => e.stopPropagation()}
                            title={`Open ${customer.name || customer.email} profile`}>
                            {customer.name}
                          </a>
                        ) : (
                          <div className={s.customerName}>{customer.name}</div>
                        );
                      })()}
                      <div className={s.customerEmail}>{customer.email}</div>
                    </td>
                    <td>
                      <span className={s.typeText}>{typeLabel}</span>
                    </td>
                    <td>
                      {row.paymentSession?.paymentDescriptor ? (
                        <span
                          className={s.subscriptionText}
                          title={row.paymentSession.paymentDescriptor}>
                          {row.paymentSession.paymentDescriptor}
                        </span>
                      ) : (
                        <span className={s.brandCodeEmpty}>N/A</span>
                      )}
                    </td>
                    <td>
                      {(() => {
                        const subscriptionLabel = getPaymentSubscriptionLabel(row);
                        return subscriptionLabel ? (
                          <span className={s.subscriptionText} title={subscriptionLabel}>
                            {subscriptionLabel}
                          </span>
                        ) : (
                          <span className={s.brandCodeEmpty}>N/A</span>
                        );
                      })()}
                    </td>
                    <td>
                      {row.brand?.brandCode ? (
                        <span className={s.brandCode}>{row.brand.brandCode.toUpperCase()}</span>
                      ) : (
                        <span className={s.brandCodeEmpty}>N/A</span>
                      )}
                    </td>
                    <td className={s.amount}>
                      <span className={s.amountValue}>{amountLabel}</span>
                      <span className={s.amountCurrency}>{row.currency.code}</span>
                    </td>
                    <td>
                      <div className={s.statusCell}>
                        <PaymentStatusBadge status={uiStatus} />
                        {declineLine && (
                          <span className={s.declineLine} title={declineLine}>
                            {declineLine}
                          </span>
                        )}
                        {captureState && (
                          <span className={s.captureChip} data-capture-state={captureState}>
                            {CAPTURE_STATE_LABELS[captureState]}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showOverlay && (
        <div className={s.fetchingOverlay} aria-live="polite" aria-busy="true">
          <LoadingAnimation compact />
        </div>
      )}
    </div>
  );
}

export default PaymentsTable;
